import { MILLISECONDS_IN_A_MINUTE } from 'constants/time';
import { useCallback, useEffect, useRef } from 'react';
type InactivityProps = {
  callback: () => void;
  isActive: boolean;
};

export const useInactivity = ({ callback, isActive }: InactivityProps) => {
  const timeout = useRef<NodeJS.Timeout | undefined>();

  // stops the currently active timeout and starts a new one right after
  const clearInactivity = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  // Starts a new inactivity timeout that will execute given callback
  const restartInactivityTimeout = useCallback(() => {
    // clear previous timeouts
    clearInactivity();

    timeout.current = setTimeout(
      () => {
        callback();
      },
      // callback,
      Number(process.env.NX_INACTIVITY_TIMEOUT) * MILLISECONDS_IN_A_MINUTE,
    );
  }, [timeout, callback, clearInactivity]);

  useEffect(() => {
    // return early if the tracker is supposed to be active to prevent unwanted timeouts
    if (!isActive) {
      return;
    }

    // start an initial timeout
    restartInactivityTimeout();

    // register a refresh of the timeout on each user-activity event
    const activityEvents = ['click', 'keydown', 'mousemove'];
    activityEvents.forEach((event) => {
      document.addEventListener(event, restartInactivityTimeout);
    });

    // remove all registered eventListener and clear the current timeout when component is being unmounted
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, restartInactivityTimeout);
      });

      clearInactivity();
    };
  }, [clearInactivity, restartInactivityTimeout, isActive]);
};
